/* eslint-disable camelcase */
import castArray from 'lodash/castArray';
import capitalize from 'lodash/capitalize';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';

import { getImage } from './media';

const defaultDeserializeError = (field, errors) => {
  const fieldErrors = errors[field.name];

  return fieldErrors ? { name: field.name, errors: fieldErrors } : null;
};

export const transformApiFormErrors = (errors = {}, formFields = []) => {
  const transformedErrors = formFields.reduce((acc, { deserializeError = defaultDeserializeError, ...field }) => {
    const deserializedError = deserializeError(field, errors);

    if (deserializedError) {
      // cast array because we can return one error for single field and also array ofr errors for array field
      acc.push(...castArray(deserializedError));
    }

    return acc;
  }, []);

  return transformedErrors;
};

const maybeGetField = (field, values) => {
  const value = values[field.name];
  return value ? { [field.name]: value } : null;
};
export const deserializeFormValues = (data = {}, formFields) => {
  const deserializedValues = formFields.reduce((acc, field) => {
    if (typeof field !== 'object') return acc;

    const { deserialize = maybeGetField, ...restField } = field;
    const deserializedValue = deserialize(restField, data);

    if (deserializedValue) {
      return { ...acc, ...deserializedValue };
    }

    return acc;
  }, {});

  return deserializedValues;
};

export const serializeFormValues = (formValues, formFields) => {
  const serializedValues = formFields.reduce((acc, field) => {
    if (typeof field !== 'object') return acc;

    const { serialize = maybeGetField, ...restField } = field;
    const serializedValue = serialize(restField, formValues);

    if (serializedValue) {
      return { ...acc, ...serializedValue };
    }

    return acc;
  }, {});

  return serializedValues;
};

export const relatedEntityArraysSerializer = (field, values) => {
  const value = (values[field.name] ?? []).map((item) => item.id);
  return { [field.name]: value };
};

const defaultTransformKey = (field) => `${field.name}Id`;
export const selectSerializer = (field, values, { transformKey = defaultTransformKey } = {}) => {
  const value = values[field.name]?.value;
  return value ? { [`${transformKey(field)}`]: value } : null;
};

const appendKey = (base, key) => `${base}${capitalize(key)}`;
export const relatedEntitySerializer = (field, values, keys = ['id']) => {
  const entity = keys.reduce((acc, _key) => {
    const originalKey = _key?.key || _key;
    const renamedKey = _key?.as || _key;

    const value = values[field.name]?.[originalKey];
    if (value) {
      acc[appendKey(field.name, renamedKey)] = value;
    }
    return acc;
  }, {});

  return Object.keys(entity).length ? entity : null;
};
export const makeRelatedEntitySerializer = (keys) => (field, data) => relatedEntitySerializer(field, data, keys);

export const relatedInsideTrackSerializer = (field, values, keys = ['id', 'slug']) => {
  const entity = keys.reduce((acc, _key) => {
    const originalKey = _key?.key || _key;
    const renamedKey = _key?.as || _key;

    const value = values[field.name]?.[originalKey];
    if (value) {
      acc[renamedKey === 'slug' ? renamedKey : appendKey(field.name, renamedKey)] = value;
    }
    return acc;
  }, {});

  return Object.keys(entity).length ? entity : null;
};

export const relatedEntitySerializerForContentId = (field, values, keys = ['contentId']) => {
  const entity = keys.reduce((acc, key) => {
    const value = values[field.name]?.[key] ? values[field.name]?.[key] : values[field.name]?.['id'];
    if (value) {
      acc[appendKey(field.name, 'id')] = value;
    }
    return acc;
  }, {});

  return Object.keys(entity).length ? entity : null;
};

export const relatedEntityDeserializer = (field, data, keys = ['id', 'name']) => {
  const entity = keys.reduce((acc, _key) => {
    const originalKey = _key?.key || _key;
    const renamedKey = _key?.as || _key;

    const value = data[appendKey(field.name, renamedKey)];
    if (value) {
      acc[originalKey] = value;
    }
    return acc;
  }, {});

  return Object.keys(entity).length ? { [field.name]: entity } : null;
};
export const makeRelatedEntityDeserializer = (keys) => (field, data) => relatedEntityDeserializer(field, data, keys);

const defaultGetValue = (field, data) => data[`${field.name}Id`];
const defaultGetLabel = (field, data) => data[`${field.name}Name`];
export const selectDeserializer = (field, data, { getValue = defaultGetValue, getLabel = defaultGetLabel } = {}) => {
  const value = getValue(field, data);
  const label = getLabel(field, data);

  return value ? { [field.name]: { value, label } } : null;
};

export const isFormValidationError = (e) => !(e instanceof Error) && e.errorFields;

export const deserializeErrorRelatedField = (field, errors) => {
  const fieldErrors = errors[`${field.name}Id`];

  return fieldErrors ? { name: field.name, errors: fieldErrors } : null;
};

export const deserializeContentModulesErrors = (field, errors) => {
  const contentModuleErrors = [];

  Object.entries(errors).forEach(([key, error]) => {
    if (!key.startsWith('contentModule')) return;

    // Extract array index, type | content, fields errors with dot notation
    // E.g [0, 'content', 'article.image.id']
    const match = key.match(/^contentModules\[(\d+)\]\.(.*?)(?=\.(.*)|$)/);

    if (!match) return;

    const [
      ,
      index,
      // typeOrContentKey,
      // fieldKey
    ] = match;
    const parsedIndex = Number.parseInt(index, 10);

    contentModuleErrors[parsedIndex] = error;

    // Use this if we need to insert errors into fields
    // contentModuleErrors[parsedIndex] = {
    //   [typeOrContentKey]: fieldKey ? { [fieldKey]: error } : error,
    // };
  });

  return contentModuleErrors.length > 0 ? { name: 'contentModules', errors: contentModuleErrors } : null;
};

const defaultLenMessage = (len) => `Should have ${len} items`;
const defaultMinMessage = (min) => `Should have at least ${min} item${min > 1 ? 's' : ''}`;
const defaultMaxMessage = (max) => `Should have max ${max} item${max > 1 ? 's' : ''}`;

const validateListRule = (rule, validationFn, defaultMessage) => {
  const ruleValue = rule?.value || rule;
  const ruleMessage = rule?.message || defaultMessage;

  if (!rule) return null;

  if (validationFn(ruleValue)) {
    return ruleMessage(ruleValue);
  }

  return null;
};

export const fieldListValidator = ({ min, max, len, transform } = {}) => ({
  min,
  max,
  len,
  validator: (_, field = []) => {
    const fieldArray = transform ? transform(field) : field;
    const errors = [
      validateListRule(len, (ruleValue) => ruleValue !== fieldArray.length, defaultLenMessage),
      validateListRule(min, (ruleValue) => fieldArray.length < ruleValue, defaultMinMessage),
      validateListRule(max, (ruleValue) => fieldArray.length > ruleValue, defaultMaxMessage),
    ];

    const error = errors.find(Boolean);
    if (error) {
      return Promise.reject(error);
    }

    return Promise.resolve();
  },
});

export const entityOrUrlFieldSerialize = (field, values) => {
  const setId = values[field.entityName]?.contentId || values[field.entityName]?.id;
  return pickBy(
    {
      [`${field.entityName}Id`]: setId,
      [field.urlName]: values[field.urlName],
    },
    identity,
  );
};

export const entityOrUrlFieldDeserialize = (field, data) => {
  const idKey = [`${field.entityName}Id`];

  if (data[idKey]) return { [idKey]: data[idKey] };
  if (data[field.urlName]) return { [field.urlName]: data[field.urlName] };

  return {};
};

export const relatedContentSerializer = (field, values, key = 'id') => {
  const value = values[field.name];

  return value ? { [`${field.name}Ids`]: value.map(({ [key]: _id }) => _id) } : null;
};

export const relatedContentSerializerForContentIdOrId = (field, values) => {
  const value = values[field.name];
  return value
    ? {
        [`${field.name}Ids`]: value.map(({ id: _id }) => {
          const idArray = _id.split('_');
          return idArray[0];
        }),
      }
    : null;
};

export const relatedEntitySerializerForContentIdOrId = (field, values, keys = ['id']) => {
  const entity = keys.reduce((acc, _key) => {
    const originalKey = _key?.key || _key;
    const renamedKey = _key?.as || _key;

    const value = values[field.name]?.[originalKey];
    const idArray = value && value.split('_');
    if (value) {
      acc[appendKey(field.name, renamedKey)] = idArray[0];
    }
    return acc;
  }, {});

  return Object.keys(entity).length ? entity : null;
};

export const makeRelatedContentSerializer = (key) => (field, data) => relatedContentSerializer(field, data, key);
export const multilingualRelatedContentSerializer = makeRelatedContentSerializer('contentId');
export const multilingualRelatedContentIdSerializer = (field, values) => {
  const _field = values[field.name];

  return _field ? { [`${field.name}Ids`]: _field.map((fieldItem) => fieldItem.contentId ?? fieldItem.id) } : null;
};

export const deserializeErrorRelatedContent = (field, errors) => {
  const fieldErrors = errors[`${field.name}Ids`];

  return fieldErrors ? { name: field.name, errors: fieldErrors } : null;
};

export const imageDeserialize = (field, data) => {
  const { id, edited, fileName } = getImage(data, field.name);

  if (!id) return null;

  return { [field.name]: { id, edited, fileName } };
};

export const imageSerialize = (field, values) => {
  return {
    [`${field.name}Id`]: values[field.name]?.id,
    [`${field.name}Edited`]: values[field.name]?.edited,
    [`${field.name}FileName`]: values[field.name]?.fileName,
  };
};
